import { useEffect } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import '@/shared/features/board/consts/events.quiz-board-login';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { ChevronLeftIcon, GlobeIcon, LoginIcon } from '@/icons';
import { MyMenuDropdown } from '@/layout/Header/MyMenuDropdown';
import { useUser } from '@/lib/auth';
import { fullscreenLoading } from '@/shared/components/FullscreenLoading';
import { LanguageSelector } from '@/shared/components/LanguageSelector';
import { TemplatePicker } from '@/shared/features/board/components/TemplatePicker';
import { useBoardCreation } from '@/shared/features/board/hooks/useBoardCreation';
import { BOARD_ENTRY_FROM, getBoardPath } from '@/shared/features/board/utils/boardEntryRoute';
import { useAutoLinkToClassroom } from '@/shared/features/classroom/hooks';
import { Button, IconButton } from '@/ui_v3';
export function NewBoardPageView() {
  const router = useRouter();
  const {
    t
  } = useTranslation('board');
  const {
    user,
    isInitialized
  } = useUser();
  const {
    autoLink
  } = useAutoLinkToClassroom();

  /**
   * 학급 콘텐츠 연결 모달의 "새 보드 만들기"로 진입했을 때만 실린다.
   * 이 값이 있으면 생성 완료 시 해당 학급으로 자동 연결한다.
   */
  const classroomId = typeof router.query.classroomId === 'string' ? router.query.classroomId : undefined;
  const {
    createBlankBoard,
    createFromTemplate,
    isCreating
  } = useBoardCreation({
    source: 'board_new_page',
    onCreated: async board => {
      // 학급 맥락으로 진입했으면 이동 전에 연결까지 끝낸다. 이동 후 연결하면
      // 화면이 바뀌는 도중 토스트가 떠 어느 화면의 결과인지 읽기 어렵다.
      // autoLink 는 실패해도 throw 하지 않으므로 이동이 막히지 않는다.
      if (classroomId) {
        await autoLink({
          classroomId,
          contentId: board.id,
          contentType: 'BOARD'
        });
      }

      // 갓 만든 보드는 이름·배경·공개 설정을 먼저 만지게 되므로 설정 패널을
      // 연 채로 들여보낸다.
      const boardPath = getBoardPath(board.id, {
        from: classroomId ? BOARD_ENTRY_FROM.CLASSROOM : BOARD_ENTRY_FROM.MY_BOARD,
        settings: true
      });
      try {
        await router.push(boardPath);
      } catch (error) {
        if ((error as {
          cancelled?: boolean;
        }).cancelled) return;
        await router.replace(boardPath).catch(() => false);
      }
    }
  });
  useEffectOnce(() => {
    logger.track('quiz_board_creation_page_view');
  });

  // 로딩 오버레이도 복제 흐름과 동일하게 임퍼러티브 fullscreenLoading 으로 통일.
  useEffect(() => {
    if (isCreating) {
      fullscreenLoading.open();
    } else {
      fullscreenLoading.close();
    }
    return () => fullscreenLoading.close();
  }, [isCreating]);
  return <main className="relative min-h-screen bg-neutral" aria-busy={isCreating} data-sentry-component="NewBoardPageView" data-sentry-source-file="NewBoardPageView.tsx">
      <header className="flex h-[60px] items-center justify-between border-b border-alternative bg-white px-md tablet-sm:h-[64px] tablet-sm:px-[40px]">
        <div className="flex min-w-0 items-center gap-md">
          <IconButton size="md" variant="outlined" intent="neutral" onClick={() => router.back()} data-sentry-element="IconButton" data-sentry-source-file="NewBoardPageView.tsx">
            <ChevronLeftIcon size="md" data-sentry-element="ChevronLeftIcon" data-sentry-source-file="NewBoardPageView.tsx" />
          </IconButton>
          <h1 className="min-w-0 truncate text-body-lg-bold text-default">
            {t('board.new.title')}
          </h1>
        </div>
        {isInitialized && (user ? <MyMenuDropdown hideBadge /> : <GuestHeaderActions />)}
      </header>

      <div className="mx-auto flex w-full max-w-[900px] flex-col px-md py-2xl tablet-sm:px-0">
        <TemplatePicker onCreateBlank={createBlankBoard} onSelect={createFromTemplate} disabled={isCreating} data-sentry-element="TemplatePicker" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>
    </main>;
}
const GuestHeaderActions = () => {
  const router = useRouter();
  const {
    t
  } = useTranslation('board');
  const loginHref = {
    pathname: '/login',
    query: {
      callbackUrl: router.asPath
    }
  };

  // 보드 생성 화면은 아직 board_id가 없어 location만 남긴다(header/modal과 구분).
  const handleLoginClick = () => {
    trackEvent('board_login_click', {
      location: 'creation_header'
    });
  };
  return <div className="flex shrink-0 items-center gap-xs" data-sentry-component="GuestHeaderActions" data-sentry-source-file="NewBoardPageView.tsx">
      <Link href={loginHref} className="tablet-sm:hidden" onClick={handleLoginClick} data-sentry-element="Link" data-sentry-source-file="NewBoardPageView.tsx">
        <IconButton variant="transparent" intent="neutral" size="md" data-sentry-element="IconButton" data-sentry-source-file="NewBoardPageView.tsx">
          <LoginIcon size="md" fill={ICON_COLOR.neutral} data-sentry-element="LoginIcon" data-sentry-source-file="NewBoardPageView.tsx" />
        </IconButton>
      </Link>
      <Link href={loginHref} className="hidden tablet-sm:block" onClick={handleLoginClick} data-sentry-element="Link" data-sentry-source-file="NewBoardPageView.tsx">
        <Button variant="transparent" intent="neutral" size="md" className="rounded-full" data-sentry-element="Button" data-sentry-source-file="NewBoardPageView.tsx">
          {t('board.header.login')}
        </Button>
      </Link>
      <div className="tablet-sm:hidden">
        <LanguageSelector size="md" variant="transparent" intent="neutral" eventProps={{
        location: 'board_new_header'
      }} trigger={<IconButton variant="transparent" intent="neutral" size="md">
              <GlobeIcon size="md" fill={ICON_COLOR.neutral} />
            </IconButton>} data-sentry-element="LanguageSelector" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>
      <div className="hidden tablet-sm:flex">
        <LanguageSelector size="md" variant="transparent" intent="neutral" rounded="full" eventProps={{
        location: 'board_new_header'
      }} data-sentry-element="LanguageSelector" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>
    </div>;
};